import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { SlideProps } from './types';
import { useSlideTimeout } from './useSlideTimeout';
import { createComponentLogger } from './logger';

// Configure PDF.js worker (bundled version)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// PDFSlide: Displays first page of PDF and auto-completes after duration
const PDFSlide: React.FC<SlideProps> = ({ source, isActive, defaultDuration, onComplete, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 800, height: 600 });
  const [isPDFLoaded, setIsPDFLoaded] = useState(false);
  const [isPageRendered, setIsPageRendered] = useState(false);

  // Create logger instance once
  const log = useMemo(() => createComponentLogger('PDFSlide'), []);

  // Log component lifecycle
  useEffect(() => {
    log('🎬 PDFSlide mounted', {
      isActive,
      sourceUrl: source.url,
      defaultDuration,
      customDuration: source.duration
    });
  }, [log, isActive, source.url, source.duration, defaultDuration]);

  // Log active state changes
  useEffect(() => {
    if (isActive) {
      log('▶️  PDFSlide activated', { url: source.url });
    } else {
      log('⏸️  PDFSlide deactivated');
    }
  }, [isActive, log, source.url]);

  // Log PDF loaded state changes
  useEffect(() => {
    log('📊 PDF load state changed', { isPDFLoaded });
  }, [isPDFLoaded, log]);

  // Log page rendered state changes
  useEffect(() => {
    log('📄 Page render state changed', { isPageRendered });
  }, [isPageRendered, log]);

  // Log content readiness
  useEffect(() => {
    const isContentReady = isPDFLoaded && isPageRendered;
    log('🔍 Content readiness check', { isPDFLoaded, isPageRendered, isContentReady });
  }, [isPDFLoaded, isPageRendered, log]);

  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      const newDimensions = {
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      };

      // Only log if dimensions actually changed
      if (
        newDimensions.width !== containerDimensions.width ||
        newDimensions.height !== containerDimensions.height
      ) {
        log('📐 Container dimensions updated', newDimensions);
      }

      setContainerDimensions(newDimensions);
    }
  }, [containerDimensions.width, containerDimensions.height, log]);

  useEffect(() => {
    log('⚙️  Setting up dimension monitoring');
    const timer = setInterval(updateDimensions, 100);
    window.addEventListener('resize', updateDimensions);

    return () => {
      log('🧹 Cleaning up dimension monitoring');
      window.removeEventListener('resize', updateDimensions);
      clearInterval(timer);
    };
  }, [log, updateDimensions]);

  // Use the shared timeout hook
  // Content is ready when both PDF is loaded AND first page is rendered
  useSlideTimeout({
    isActive,
    isContentReady: isPDFLoaded && isPageRendered,
    contentDuration: source.duration,
    defaultDuration,
    onComplete,
    componentName: 'PDFSlide',
    log,
  });

  const handleDocumentLoadSuccess = () => {
    log('✅ PDF document loaded successfully', { url: source.url });
    setIsPDFLoaded(true);
  };

  const handleDocumentLoadError = (error: Error) => {
    log('❌ PDF document failed to load', {
      url: source.url,
      error: error.message
    });
    onError(error);
  };

  const handlePageRenderSuccess = () => {
    log('✅ PDF page rendered successfully', { pageNumber: 1 });
    setIsPageRendered(true);
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Document
        file={source.url}
        loading={<></>}
        onLoadError={handleDocumentLoadError}
        onLoadSuccess={handleDocumentLoadSuccess}
      >
        <Page
          pageNumber={1}
          height={containerDimensions.height}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={handlePageRenderSuccess}
        />
      </Document>
    </div>
  );
};

export default PDFSlide;
